import {
  CalendarStringEnum,
  CalendarUnitEnum,
  HighWaterMarkEnum,
} from '../enums/openEnd.enum';
import {
  CloseDateTypeEnum,
  CoInvestPolicyEnum,
  CommitmentsTypeEnum,
  MonthEnum,
  PlacementAgentEnum,
  RankEnum,
} from '../enums/closedEnd.enum';
import {
  CurrencyEnum,
  DiscountTypeEnum,
  FundLevelsEnum,
  FundStructureEnum,
  MgmtFeeFrequencyEnum,
  ProgramStatusEnum,
  SimpleAnswerEnum,
  ActiveTimePeriodEnum,
} from '../enums/entity.enum';
import {
  CELL_IMPORT_STRATEGY_CONFIG,
  CELL_VALUE_TYPE_CONFIG,
  COMMON_EXCEL_WORKBOOK_CONFIG,
  FULL_SHEET_PROTECTION_CONFIG,
  SHEET_STATE_CONFIG,
  TWorkbookConfig,
  WORKBOOK_IMPORT_STRATEGY_CONFIG,
} from '@aksia/infrastructure';

const COMMON_TEMPLATE_EXCEL_WORKBOOK_CONFIG = {
  ...COMMON_EXCEL_WORKBOOK_CONFIG,
  category: 'Template Streams',
  subject: '1.0',
  description: 'Import / Export file for terms.',
  manager: 'datateam@aksia.com',
  importStrategy: WORKBOOK_IMPORT_STRATEGY_CONFIG.CLIENT,
};

const COMMON_IV_TEMPLATE_EXCEL_CELLS_CONFIG = [
  {
    cell: 'Global_Asof',
    field: 'Global_Asof',
    valueType: CELL_VALUE_TYPE_CONFIG.DATE,
    mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
  },
  {
    cell: 'InvestmentVehicle.primaryRegionId',
    field: 'primaryRegionId',
    valueType: CELL_VALUE_TYPE_CONFIG.STRING,
    mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
    onImport: '_onImport_PrimaryRegionId',
  },
  {
    cell: 'InvestmentVehicle.assetClass',
    field: 'assetClass',
    valueType: CELL_VALUE_TYPE_CONFIG.STRING,
    mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
  },
  {
    cell: 'InvestmentVehicle.fundOverview',
    field: 'fundOverview',
    valueType: CELL_VALUE_TYPE_CONFIG.STRING,
    mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
  },
];

export const COMPANY_TEMPLATE_WORKBOOK_CONFIG: TWorkbookConfig = {
  ...COMMON_TEMPLATE_EXCEL_WORKBOOK_CONFIG,
  sheets: [
    {
      sheetName: 'Information',
      state: SHEET_STATE_CONFIG.VISIBLE,
      data: [],
      conditionalFormatting: [],
      protection: FULL_SHEET_PROTECTION_CONFIG,
      cells: [
        {
          cell: 'ManagementCompany.inceptionDate',
          field: 'inceptionDate',
          valueType: CELL_VALUE_TYPE_CONFIG.DATE,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
        },
        {
          cell: 'ManagementCompany.totalStaff',
          field: 'totalStaff',
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
        },
        {
          cell: 'companyAUM.value',
          field: 'value',
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
        },
        {
          cell: 'companyAUM.currency',
          field: 'currency',
          valueType: CELL_VALUE_TYPE_CONFIG.STRING,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
        },
        {
          cell: 'companyAUM.asOf',
          field: 'asOf',
          valueType: CELL_VALUE_TYPE_CONFIG.DATE,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
        },
        {
          cell: 'ManagementCompany.addresses..address1',
          field: 'address1',
          valueType: CELL_VALUE_TYPE_CONFIG.STRING,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport: '_onImport_Address1',
        },
        {
          cell: 'ManagementCompany.addresses..address2',
          field: 'address2',
          valueType: CELL_VALUE_TYPE_CONFIG.STRING,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
        },
        {
          cell: 'ManagementCompany.addresses..city',
          field: 'city',
          valueType: CELL_VALUE_TYPE_CONFIG.STRING,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
        },
        {
          cell: 'ManagementCompany.addresses..country',
          field: 'country',
          valueType: CELL_VALUE_TYPE_CONFIG.STRING,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
        },
        {
          cell: 'ManagementCompany.addresses..state',
          field: 'state',
          valueType: CELL_VALUE_TYPE_CONFIG.STRING,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
        },
        {
          cell: 'ManagementCompany.addresses..zip',
          field: 'zip',
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
        },
        {
          cell: 'ManagementCompany.addresses..phone',
          field: 'phone',
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
        },
        {
          cell: 'ManagementCompany.url',
          field: 'url',
          valueType: CELL_VALUE_TYPE_CONFIG.STRING,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
        },
        {
          cell: 'ManagementCompany.generalEmail',
          field: 'generalEmail',
          valueType: CELL_VALUE_TYPE_CONFIG.STRING,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
        },
      ],
    },
  ],
};

export const PUBLIC_TEMPLATE_EXCEL_WORKBOOK_CONFIG: TWorkbookConfig = {
  ...COMMON_TEMPLATE_EXCEL_WORKBOOK_CONFIG,
  sheets: [
    {
      sheetName: 'Information',
      state: SHEET_STATE_CONFIG.VISIBLE,
      data: [],
      conditionalFormatting: [],
      protection: FULL_SHEET_PROTECTION_CONFIG,
      cells: [
        ...COMMON_IV_TEMPLATE_EXCEL_CELLS_CONFIG,
        {
          cell: 'InvestmentVehicle.legalStructure',
          field: 'legalStructure',
          valueType: CELL_VALUE_TYPE_CONFIG.STRING,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport: '_onImport_LegalStructure',
        },
        {
          cell: 'InvestmentVehicle.masterFunds.0.name',
          field: 'name',
          valueType: CELL_VALUE_TYPE_CONFIG.STRING,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
        },
        {
          cell: 'InvestmentVehicle.masterFunds.1.name',
          field: 'name',
          valueType: CELL_VALUE_TYPE_CONFIG.STRING,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
        },
        {
          cell: 'InvestmentVehicle.dateOfFormation',
          field: 'dateOfFormation',
          valueType: CELL_VALUE_TYPE_CONFIG.DATE,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
        },
        {
          cell: 'InvestmentVehicle.commencementOfOperations',
          field: 'commencementOfOperations',
          valueType: CELL_VALUE_TYPE_CONFIG.DATE,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
        },
        {
          cell: 'Shareclass.hasSidePocket',
          field: 'hasSidePocket',
          valueType: CELL_VALUE_TYPE_CONFIG.STRING,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport: '_onImport_HasSidePocket',
        },
        {
          cell: 'Shareclass.sidePocketOpt',
          field: 'sidePocketOpt',
          valueType: CELL_VALUE_TYPE_CONFIG.STRING,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport(value) {
            return SimpleAnswerEnum.toOptions().find(
              (option) => option.label === value,
            )?.value;
          },
        },
        {
          cell: 'Shareclass.sidePockets.0.sidePocketLevel',
          field: 'sidePocketLevel',
          valueType: CELL_VALUE_TYPE_CONFIG.STRING,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport(value) {
            return FundLevelsEnum.toOptions().find(
              (option) => option.label === value,
            )?.value;
          },
        },
        {
          cell: 'Shareclass.sidePockets.0.sidePocketLimit',
          field: 'sidePocketLimit',
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport(value) {
            return Number.isFinite(value) ? (value as number) * 100 : undefined;
          },
        },
        {
          cell: 'Shareclass.minimumInitialInvestment',
          field: 'minimumInitialInvestment',
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
        },
        {
          cell: 'Shareclass.minimumAdditionalInvestment',
          field: 'minimumAdditionalInvestment',
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
        },
        {
          cell: 'Shareclass.minimumInvestmentCurrency',
          field: 'minimumInvestmentCurrency',
          valueType: CELL_VALUE_TYPE_CONFIG.STRING,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport(value) {
            return CurrencyEnum.toOptions().find(
              (option) => option.label === value,
            )?.value;
          },
        },
        {
          cell: 'Shareclass.hasSubscriptionFee',
          field: 'hasSubscriptionFee',
          valueType: CELL_VALUE_TYPE_CONFIG.STRING,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport: '_onImport_HasSubscriptionFee',
        },
        {
          cell: 'Shareclass.subscriptionFee',
          field: 'subscriptionFee',
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport(value) {
            return Number.isFinite(value) ? (value as number) * 100 : undefined;
          },
        },
        {
          cell: 'Shareclass.hasLockup',
          field: 'hasLockup',
          valueType: CELL_VALUE_TYPE_CONFIG.STRING,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport: '_onImport_HasLockup',
        },
        {
          cell: 'Shareclass.lockups.0.lockupDurationMinRange',
          field: 'lockupDurationMinRange',
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
        },
        {
          cell: 'Shareclass.lockups.0.lockupDurationMaxRange',
          field: 'lockupDurationMaxRange',
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
        },
        {
          cell: 'Shareclass.lockups.0.lockupType',
          field: 'lockupType',
          valueType: CELL_VALUE_TYPE_CONFIG.STRING,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport: '_onImport_LockupType',
        },
        {
          cell: 'Shareclass.lockups.0.lockupFee',
          field: 'lockupFee',
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport(value) {
            return Number.isFinite(value) ? (value as number) * 100 : undefined;
          },
        },
        {
          cell: 'Shareclass.redemptionTermsFrequencyAmount',
          field: 'redemptionTermsFrequencyAmount',
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
        },
        {
          cell: 'Shareclass.redemptionTermsFrequency',
          field: 'redemptionTermsFrequency',
          valueType: CELL_VALUE_TYPE_CONFIG.STRING,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport(value) {
            return CalendarUnitEnum.toOptions().find(
              (option) => option.label === value,
            )?.value;
          },
        },
        {
          cell: 'Shareclass.redemptionCustomDates',
          field: 'redemptionCustomDates',
          valueType: CELL_VALUE_TYPE_CONFIG.NULL,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
        },
        {
          cell: 'Shareclass.redemptionTermsFrequencyDay',
          field: 'redemptionTermsFrequencyDay',
          valueType: CELL_VALUE_TYPE_CONFIG.STRING,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
        },
        {
          cell: 'Shareclass.redemptionTermsNotice',
          field: 'redemptionTermsNotice',
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
        },
        {
          cell: 'Shareclass.redemptionTermsNoticePeriod',
          field: 'redemptionTermsNoticePeriod',
          valueType: CELL_VALUE_TYPE_CONFIG.STRING,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport(value) {
            return ActiveTimePeriodEnum.toOptions().find(
              (option) => option.label === value,
            )?.value;
          },
        },
        {
          cell: 'Shareclass.redemptionFee',
          field: 'redemptionFee',
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport(value) {
            return Number.isFinite(value) ? (value as number) * 100 : undefined;
          },
        },
        {
          cell: 'Shareclass.hasGate',
          field: 'hasGate',
          valueType: CELL_VALUE_TYPE_CONFIG.STRING,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport: '_onImport_HasGate',
        },
        {
          cell: 'Shareclass.gates.0.gateLevel',
          field: 'gateLevel',
          valueType: CELL_VALUE_TYPE_CONFIG.STRING,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport: '_onImport_GateLevel',
        },
        {
          cell: 'Shareclass.gates.0.gatePercentage',
          field: 'gatePercentage',
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport(value) {
            return Number.isFinite(value) ? (value as number) * 100 : undefined;
          },
        },
        {
          cell: 'Shareclass.gates.0.gateMasterFundPercentage',
          field: 'gateMasterFundPercentage',
          valueType: CELL_VALUE_TYPE_CONFIG.NULL,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport(value) {
            return Number.isFinite(value) ? (value as number) * 100 : undefined;
          },
        },
        {
          cell: 'Shareclass.gates.0.gateFundPercentage',
          field: 'gateFundPercentage',
          valueType: CELL_VALUE_TYPE_CONFIG.NULL,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport(value) {
            return Number.isFinite(value) ? (value as number) * 100 : undefined;
          },
        },
        {
          cell: 'Shareclass.gates.0.gateInvestorPercentage',
          field: 'gateInvestorPercentage',
          valueType: CELL_VALUE_TYPE_CONFIG.NULL,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport(value) {
            return Number.isFinite(value) ? (value as number) * 100 : undefined;
          },
        },
        {
          cell: 'Shareclass.holdback',
          field: 'holdback',
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport(value) {
            return Number.isFinite(value) ? (value as number) * 100 : undefined;
          },
        },
        {
          cell: 'Shareclass.hasManagementFee',
          field: 'hasManagementFee',
          valueType: CELL_VALUE_TYPE_CONFIG.STRING,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport: '_onImport_HasManagementFee',
        },
        {
          cell: 'Shareclass.mgmtFeeRateType',
          field: 'mgmtFeeRateType',
          valueType: CELL_VALUE_TYPE_CONFIG.STRING,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport: '_onImport_MgmtFeeRateType',
        },
        {
          cell: 'Shareclass.mgmtFeeRates.0.mgmtFeeRateAmountFrom',
          field: 'mgmtFeeRateAmountFrom',
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
        },
        {
          cell: 'Shareclass.mgmtFeeRates.0.mgmtFeeRateAmountTo',
          field: 'mgmtFeeRateAmountTo',
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
        },
        {
          cell: 'Shareclass.mgmtFeeRates.0.mgmtFeeRatePercentage',
          field: 'mgmtFeeRatePercentage',
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport(value) {
            return Number.isFinite(value) ? (value as number) * 100 : undefined;
          },
        },
        {
          cell: 'Shareclass.payable',
          field: 'payable',
          valueType: CELL_VALUE_TYPE_CONFIG.STRING,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport: '_onImport_Payable',
        },
        {
          cell: 'Shareclass.hasManagementFeeDiscounts',
          field: 'hasManagementFeeDiscounts',
          valueType: CELL_VALUE_TYPE_CONFIG.STRING,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport: '_onImport_HasManagementFeeDiscounts',
        },
        {
          cell: 'Shareclass.managementFeeDiscounts.0.discountType',
          field: 'discountType',
          valueType: CELL_VALUE_TYPE_CONFIG.STRING,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport(value) {
            return DiscountTypeEnum.toOptions().find(
              (option) => option.label === value,
            )?.value;
          },
        },
        {
          cell: 'Shareclass.managementFeeDiscounts.0.discountRate',
          field: 'discountRate',
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport(value) {
            return Number.isFinite(value) ? (value as number) * 100 : undefined;
          },
        },
        {
          cell: 'Shareclass.managementFeeDiscounts.0.discountDesc',
          field: 'discountDesc',
          valueType: CELL_VALUE_TYPE_CONFIG.STRING,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
        },
        {
          cell: 'Shareclass.hasIncentiveFee',
          field: 'hasIncentiveFee',
          valueType: CELL_VALUE_TYPE_CONFIG.STRING,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport: '_onImport_HasIncentiveFee',
        },
        {
          cell: 'Shareclass.incFeeRateType',
          field: 'incFeeRateType',
          valueType: CELL_VALUE_TYPE_CONFIG.STRING,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport: '_onImport_IncFeeRateType',
        },
        {
          cell: 'Shareclass.incFeeRates.0.incFeeRatePercentage',
          field: 'incFeeRatePercentage',
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport(value) {
            return Number.isFinite(value) ? (value as number) * 100 : undefined;
          },
        },
        {
          cell: 'Shareclass.hasIncentiveFeeDiscounts',
          field: 'hasIncentiveFeeDiscounts',
          valueType: CELL_VALUE_TYPE_CONFIG.STRING,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport: '_onImport_HasIncentiveFeeDiscounts',
        },
        {
          cell: 'Shareclass.incentiveFeeDiscounts.0.discountType',
          field: 'discountType',
          valueType: CELL_VALUE_TYPE_CONFIG.STRING,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport(value) {
            return DiscountTypeEnum.toOptions().find(
              (option) => option.label === value,
            )?.value;
          },
        },
        {
          cell: 'Shareclass.incentiveFeeDiscounts.0.discountRate',
          field: 'discountRate',
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport(value) {
            return Number.isFinite(value) ? (value as number) * 100 : undefined;
          },
        },
        {
          cell: 'Shareclass.incentiveFeeDiscounts.0.discountDesc',
          field: 'discountDesc',
          valueType: CELL_VALUE_TYPE_CONFIG.STRING,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
        },
        {
          cell: 'Shareclass.incFeeRates.0.hasHurdle',
          field: 'hasHurdle',
          valueType: CELL_VALUE_TYPE_CONFIG.STRING,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport: '_onImport_IncFee',
        },
        {
          cell: 'Shareclass.incFeeRates.0.hurdleRateType',
          field: 'hurdleRateType',
          valueType: CELL_VALUE_TYPE_CONFIG.STRING,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport: '_onImport_IncFee',
        },
        {
          cell: 'Shareclass.incFeeRates.0.hurdleIndex',
          field: 'hurdleIndex',
          valueType: CELL_VALUE_TYPE_CONFIG.STRING,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport: '_onImport_IncFee',
        },
        {
          cell: 'Shareclass.incFeeRates.0.hurdleRate',
          field: 'hurdleRate',
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport(value) {
            return Number.isFinite(value) ? (value as number) * 100 : undefined;
          },
        },
        {
          cell: 'Shareclass.crystalization',
          field: 'crystalization',
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
        },
        {
          cell: 'Shareclass.crystalizationPeriod',
          field: 'crystalizationPeriod',
          valueType: CELL_VALUE_TYPE_CONFIG.STRING,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport(value) {
            return CalendarUnitEnum.toOptions().find(
              (option) => option.label === value,
            )?.value;
          },
        },
        {
          cell: 'Shareclass.highwaterMark',
          field: 'highwaterMark',
          valueType: CELL_VALUE_TYPE_CONFIG.STRING,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport(value) {
            return HighWaterMarkEnum.toOptions().find(
              (option) => option.label === value,
            )?.value;
          },
        },
      ],
    },
  ],
};

export const PRIVATE_TEMPLATE_WORKBOOK_CONFIG: TWorkbookConfig = {
  ...COMMON_TEMPLATE_EXCEL_WORKBOOK_CONFIG,
  sheets: [
    {
      sheetName: 'Information',
      state: SHEET_STATE_CONFIG.VISIBLE,
      data: [],
      conditionalFormatting: [],
      protection: FULL_SHEET_PROTECTION_CONFIG,
      cells: [
        ...COMMON_IV_TEMPLATE_EXCEL_CELLS_CONFIG,
        {
          cell: 'InvestmentVehicle.closedEndDetails.closedEndGPInformation.commingledFundInSeries',
          field: 'commingledFundInSeries',
          valueType: CELL_VALUE_TYPE_CONFIG.STRING,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport(value) {
            return RankEnum.toOptions().find((option) => option.label === value)
              ?.value;
          },
        },
        {
          cell: 'InvestmentVehicle.closedEndDetails.closedEndGPInformation.commingledFundByManager',
          field: 'commingledFundByManager',
          valueType: CELL_VALUE_TYPE_CONFIG.STRING,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport(value) {
            return RankEnum.toOptions().find((option) => option.label === value)
              ?.value;
          },
        },
        {
          cell: 'InvestmentVehicle.closedEndDetails.closedEndGPInformation.wmbe',
          field: 'wmbe',
          valueType: CELL_VALUE_TYPE_CONFIG.STRING,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport(value) {
            return SimpleAnswerEnum.toOptions().find(
              (option) => option.label === value,
            )?.value;
          },
        },
        {
          cell: 'InvestmentVehicle.closedEndDetails.closedEndKeyTerm.agmTiming',
          field: 'agmTiming',
          valueType: CELL_VALUE_TYPE_CONFIG.STRING,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport(value) {
            return MonthEnum.toOptions().find(
              (option) => option.label === value,
            )?.value;
          },
        },
        {
          cell: 'InvestmentVehicle.programStatus',
          field: 'programStatus',
          valueType: CELL_VALUE_TYPE_CONFIG.STRING,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport(value) {
            return ProgramStatusEnum.toOptions().find(
              (option) => option.label === value,
            )?.value;
          },
        },
        {
          cell: 'InvestmentVehicle.closedEndDetails.closedEndKeyTerm.placementAgent',
          field: 'placementAgent',
          valueType: CELL_VALUE_TYPE_CONFIG.STRING,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport(value) {
            return PlacementAgentEnum.toOptions().find(
              (option) => option.label === value,
            )?.value;
          },
        },
        {
          cell: 'InvestmentVehicle.commitmentCurrency',
          field: 'commitmentCurrency',
          valueType: CELL_VALUE_TYPE_CONFIG.STRING,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport(value) {
            return CurrencyEnum.toOptions().find(
              (option) => option.label === value,
            )?.value;
          },
        },
        {
          cell: 'InvestmentVehicle.closedEndDetails.closedEndKeyTerm.commitmentTarget',
          field: 'commitmentTarget',
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
        },
        {
          cell: 'InvestmentVehicle.closedEndDetails.closedEndKeyTerm.hasCommitmentCap',
          field: 'hasCommitmentCap',
          valueType: CELL_VALUE_TYPE_CONFIG.STRING,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport: '_onImport_HasCommitmentCap',
        },
        {
          cell: 'InvestmentVehicle.closedEndDetails.closedEndKeyTerm.commitmentCap',
          field: 'commitmentCap',
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
        },
        {
          cell: 'InvestmentVehicle.firstCloseDate',
          field: 'firstCloseDate',
          valueType: CELL_VALUE_TYPE_CONFIG.DATE,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
        },
        {
          cell: 'InvestmentVehicle.firstCloseDateType',
          field: 'firstCloseDateType',
          valueType: CELL_VALUE_TYPE_CONFIG.STRING,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport(value) {
            return CloseDateTypeEnum.toOptions().find(
              (option) => option.label === value,
            )?.value;
          },
        },
        {
          cell: 'InvestmentVehicle.finalCloseDate',
          field: 'finalCloseDate',
          valueType: CELL_VALUE_TYPE_CONFIG.DATE,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
        },
        {
          cell: 'InvestmentVehicle.finalCloseDateType',
          field: 'finalCloseDateType',
          valueType: CELL_VALUE_TYPE_CONFIG.STRING,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport(value) {
            return CloseDateTypeEnum.toOptions().find(
              (option) => option.label === value,
            )?.value;
          },
        },
        {
          cell: 'InvestmentVehicle.closedEndDetails.closedEndKeyTerm.commitmentsActual',
          field: 'commitmentsActual',
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
        },
        {
          cell: 'InvestmentVehicle.closedEndDetails.closedEndKeyTerm.commitmentsActualType',
          field: 'commitmentsActualType',
          valueType: CELL_VALUE_TYPE_CONFIG.STRING,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport(value) {
            return CommitmentsTypeEnum.toOptions().find(
              (option) => option.label === value,
            )?.value;
          },
        },
        {
          cell: 'InvestmentVehicle.closedEndDetails.closedEndKeyTerm.commitmentsAsOf',
          field: 'commitmentsAsOf',
          valueType: CELL_VALUE_TYPE_CONFIG.DATE,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
        },
        {
          cell: 'Shareclass.minimumLPCommitment',
          field: 'minimumLPCommitment',
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
        },
        {
          cell: 'InvestmentVehicle.closedEndDetails.closedEndStructure.minGPCommitmentType',
          field: 'minGPCommitmentType',
          valueType: CELL_VALUE_TYPE_CONFIG.STRING,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport: '_onImport_MinGPCommitmentType',
        },
        {
          cell: 'InvestmentVehicle.closedEndDetails.closedEndStructure.minGPCommitment',
          field: 'minGPCommitment',
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport(value) {
            return Number.isFinite(value) ? (value as number) * 100 : undefined;
          },
        },
        {
          cell: 'InvestmentVehicle.closedEndDetails.closedEndStructure.minGPCommitmentAmount',
          field: 'minGPCommitmentAmount',
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
        },
        {
          cell: 'Shareclass.hasFirstCloseDiscount',
          field: 'hasFirstCloseDiscount',
          valueType: CELL_VALUE_TYPE_CONFIG.STRING,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport(value) {
            return SimpleAnswerEnum.toOptions().find(
              (option) => option.label === value,
            )?.value;
          },
        },
        {
          cell: 'Shareclass.hasSizeThresholdDiscount',
          field: 'hasSizeThresholdDiscount',
          valueType: CELL_VALUE_TYPE_CONFIG.STRING,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport(value) {
            return SimpleAnswerEnum.toOptions().find(
              (option) => option.label === value,
            )?.value;
          },
        },
        {
          cell: 'Shareclass.hasOtherDiscount',
          field: 'hasOtherDiscount',
          valueType: CELL_VALUE_TYPE_CONFIG.STRING,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport(value) {
            return SimpleAnswerEnum.toOptions().find(
              (option) => option.label === value,
            )?.value;
          },
        },
        {
          cell: 'InvestmentVehicle.closedEndDetails.closedEndStructure.coInvestPolicy',
          field: 'coInvestPolicy',
          valueType: CELL_VALUE_TYPE_CONFIG.STRING,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport(value) {
            return CoInvestPolicyEnum.toOptions().find(
              (option) => option.label === value,
            )?.value;
          },
        },
        {
          cell: 'InvestmentVehicle.closedEndDetails.closedEndKeyTerm.subsequentCloseInterest',
          field: 'subsequentCloseInterest',
          valueType: CELL_VALUE_TYPE_CONFIG.STRING,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport: '_onImport_SubsequentCloseInterest',
        },
        {
          cell: 'InvestmentVehicle.closedEndDetails.closedEndKeyTerm.subsequentCloseInterestType',
          field: 'subsequentCloseInterestType',
          valueType: CELL_VALUE_TYPE_CONFIG.STRING,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport: '_onImport_SubsequentCloseInterestType',
        },
        {
          cell: 'InvestmentVehicle.closedEndDetails.closedEndKeyTerm.subsequentCloseInterestIndex',
          field: 'subsequentCloseInterestIndex',
          valueType: CELL_VALUE_TYPE_CONFIG.STRING,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport: '_onImport_SubsequentCloseInterestIndex',
        },
        {
          cell: 'InvestmentVehicle.closedEndDetails.closedEndKeyTerm.subsequentCloseInterestRate',
          field: 'subsequentCloseInterestRate',
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport(value) {
            return Number.isFinite(value) ? (value as number) * 100 : undefined;
          },
        },
        {
          cell: 'InvestmentVehicle.closedEndDetails.closedEndGPInformation.thirdPartyManagement',
          field: 'thirdPartyManagement',
          valueType: CELL_VALUE_TYPE_CONFIG.STRING,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport(value) {
            return SimpleAnswerEnum.toOptions().find(
              (option) => option.label === value,
            )?.value;
          },
        },
        {
          cell: 'InvestmentVehicle.closedEndDetails.closedEndTargetReturnProfile.gpTargetReturnsGross',
          field: 'gpTargetReturnsGross',
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport(value) {
            return Number.isFinite(value) ? (value as number) * 100 : undefined;
          },
        },
        {
          cell: 'InvestmentVehicle.closedEndDetails.closedEndTargetReturnProfile.gpTargetReturnsNet',
          field: 'gpTargetReturnsNet',
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport(value) {
            return Number.isFinite(value) ? (value as number) * 100 : undefined;
          },
        },
        {
          cell: 'InvestmentVehicle.closedEndDetails.closedEndTargetReturnProfile.gpTargetGrossMultiple',
          field: 'gpTargetGrossMultiple',
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
        },
        {
          cell: 'InvestmentVehicle.closedEndDetails.closedEndTargetReturnProfile.gpTargetNetMultiple',
          field: 'gpTargetNetMultiple',
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
        },
        {
          cell: 'InvestmentVehicle.closedEndDetails.closedEndKeyTerm.capitalCallLine',
          field: 'capitalCallLine',
          valueType: CELL_VALUE_TYPE_CONFIG.STRING,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport(value) {
            return SimpleAnswerEnum.toOptions().find(
              (option) => option.label === value,
            )?.value;
          },
        },
        {
          cell: 'InvestmentVehicle.closedEndDetails.closedEndTargetReturnProfile.targetAnnualDivident',
          field: 'targetAnnualDivident',
          valueType: CELL_VALUE_TYPE_CONFIG.NULL,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport(value) {
            return Number.isFinite(value) ? (value as number) * 100 : undefined;
          },
        },
        {
          cell: 'InvestmentVehicle.closedEndDetails.closedEndKeyTerm.contractualInvestmentPeriodExpirationYears',
          field: 'contractualInvestmentPeriodExpirationYears',
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport: '_onImport_ContractualInvestmentPeriodExpirationYears',
        },
        {
          cell: 'InvestmentVehicle.closedEndDetails.closedEndKeyTerm.contractualInvestmentPeriodExpirationType',
          field: 'contractualInvestmentPeriodExpirationType',
          valueType: CELL_VALUE_TYPE_CONFIG.STRING,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport: '_onImport_ContractualInvestmentPeriodExpirationType',
        },
        {
          cell: 'InvestmentVehicle.closedEndDetails.contractualInvestmentPeriodExtensions.0.duration',
          field: 'duration',
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
        },
        {
          cell: 'InvestmentVehicle.closedEndDetails.closedEndKeyTerm.contractualTermExpirationYears',
          field: 'contractualTermExpirationYears',
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport: '_onImport_ContractualTermExpirationYears',
        },
        {
          cell: 'InvestmentVehicle.closedEndDetails.closedEndKeyTerm.contractualTermExpirationType',
          field: 'contractualTermExpirationType',
          valueType: CELL_VALUE_TYPE_CONFIG.STRING,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport: '_onImport_ContractualTermExpirationType',
        },
        {
          cell: 'InvestmentVehicle.closedEndDetails.contractualTermExtensions.0.duration',
          field: 'duration',
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
        },
        {
          cell: 'Shareclass.mgmtFeePeriods.0.mgmtFeeTimeFrom',
          field: 'mgmtFeeTimeFrom',
          valueType: CELL_VALUE_TYPE_CONFIG.FORMULA,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport: '_onImport_MgmtFeeTimeFrom',
        },
        {
          cell: 'Shareclass.mgmtFeePeriods.0.mgmtFeeTimeTo',
          field: 'mgmtFeeTimeTo',
          valueType: CELL_VALUE_TYPE_CONFIG.FORMULA,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport: '_onImport_MgmtFeeTimeTo',
        },
        {
          cell: 'Shareclass.mgmtFeePeriods.0.mgmtFeeAmounts.0.mgmtFeeRate',
          field: 'mgmtFeeRate',
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport: '_onImport_MgmtFeeRate',
        },
        {
          cell: 'Shareclass.mgmtFeePeriods.0.mgmtFeeAmounts.0.mgmtFeeCalcOnType',
          field: 'mgmtFeeCalcOnType',
          valueType: CELL_VALUE_TYPE_CONFIG.STRING,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport: '_onImport_MgmtFeeCalcOnType',
        },
        {
          cell: 'Shareclass.mgmtFeePeriods.1.mgmtFeeTimeFrom',
          field: 'mgmtFeeTimeFrom',
          valueType: CELL_VALUE_TYPE_CONFIG.FORMULA,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport: '_onImport_MgmtFeeTimeFrom',
        },
        {
          cell: 'Shareclass.mgmtFeePeriods.1.mgmtFeeTimeTo',
          field: 'mgmtFeeTimeTo',
          valueType: CELL_VALUE_TYPE_CONFIG.FORMULA,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport: '_onImport_MgmtFeeTimeTo',
        },
        {
          cell: 'Shareclass.mgmtFeePeriods.1.mgmtFeeAmounts.0.mgmtFeeRate',
          field: 'mgmtFeeRate',
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport: '_onImport_MgmtFeeRate',
        },
        {
          cell: 'Shareclass.mgmtFeePeriods.1.mgmtFeeAmounts.0.mgmtFeeCalcOnType',
          field: 'mgmtFeeCalcOnType',
          valueType: CELL_VALUE_TYPE_CONFIG.STRING,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport: '_onImport_MgmtFeeCalcOnType',
        },
        {
          cell: 'Shareclass.mgmtFeeOffsetRate',
          field: 'mgmtFeeOffsetRate',
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport(value) {
            return Number.isFinite(value) ? (value as number) * 100 : undefined;
          },
        },
        {
          cell: 'Shareclass.carriedInterestRateType',
          field: 'carriedInterestRateType',
          valueType: CELL_VALUE_TYPE_CONFIG.STRING,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport: '_onImport_CarriedInterestRateType',
        },
        {
          cell: 'Shareclass.carriedInterestRate',
          field: 'carriedInterestRate',
          valueType: CELL_VALUE_TYPE_CONFIG.NULL,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport(value) {
            return Number.isFinite(value) ? (value as number) * 100 : undefined;
          },
        },
        {
          cell: 'Shareclass.carriedInterestPrefReturnRate',
          field: 'carriedInterestPrefReturnRate',
          valueType: CELL_VALUE_TYPE_CONFIG.NULL,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport: '_onImport_CarriedInterestPrefReturnType',
        },
        {
          cell: 'Shareclass.carriedInterestCatchUpRate',
          field: 'carriedInterestCatchUpRate',
          valueType: CELL_VALUE_TYPE_CONFIG.NULL,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport: '_onImport_CarriedInterestPrefReturnType',
        },
        {
          cell: 'Shareclass.carriedInterestTieredBaseType',
          field: 'carriedInterestTieredBaseType',
          valueType: CELL_VALUE_TYPE_CONFIG.STRING,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport: '_onImport_CarriedInterestTieredBaseType',
        },
        {
          cell: 'Shareclass.carriedInterestTieredRates.0.feeRate',
          field: 'feeRate',
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport(value) {
            return Number.isFinite(value) ? (value as number) * 100 : undefined;
          },
        },
        {
          cell: 'Shareclass.carriedInterestTieredRates.0.prefReturn',
          field: 'prefReturn',
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport(value) {
            return Number.isFinite(value) ? (value as number) * 100 : undefined;
          },
        },
        {
          cell: 'Shareclass.carriedInterestTieredRates.0.multiple',
          field: 'multiple',
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
        },
        {
          cell: 'Shareclass.carriedInterestTieredRates.0.catchupRate',
          field: 'catchupRate',
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport: '_onImport_CarriedInterestTieredCatchup',
        },
        {
          cell: 'Shareclass.carriedInterestTieredRates.1.feeRate',
          field: 'feeRate',
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport(value) {
            return Number.isFinite(value) ? (value as number) * 100 : undefined;
          },
        },
        {
          cell: 'Shareclass.carriedInterestTieredRates.1.prefReturn',
          field: 'prefReturn',
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport(value) {
            return Number.isFinite(value) ? (value as number) * 100 : undefined;
          },
        },
        {
          cell: 'Shareclass.carriedInterestTieredRates.1.multiple',
          field: 'multiple',
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
        },
        {
          cell: 'Shareclass.carriedInterestTieredRates.1.catchupRate',
          field: 'catchupRate',
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport: '_onImport_CarriedInterestTieredCatchup',
        },
        {
          cell: 'Shareclass.carriedInterestWaterfall',
          field: 'carriedInterestWaterfall',
          valueType: CELL_VALUE_TYPE_CONFIG.STRING,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport: '_onImport_CarriedInterestWaterfall',
        },
        {
          cell: 'InvestmentVehicle.fundLeverage.0.leverageBasisPercentage',
          cellRename: {
            fundLeverage: 'leverage',
            leverageBasisPercentage: 'leverageBasis',
          },
          field: 'leverageBasis',
          valueType: CELL_VALUE_TYPE_CONFIG.NULL,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport: '_onImport_LeverageBasis',
        },
        {
          cell: 'InvestmentVehicle.fundLeverage.0.leveragePercentageTarget',
          cellRename: {
            fundLeverage: 'leverage',
          },
          field: 'leveragePercentageTarget',
          valueType: CELL_VALUE_TYPE_CONFIG.NULL,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport: '_onImport_Leverage',
        },
        {
          cell: 'InvestmentVehicle.fundLeverage.0.leveragePercentageMin',
          cellRename: {
            fundLeverage: 'leverage',
          },
          field: 'leveragePercentageMin',
          valueType: CELL_VALUE_TYPE_CONFIG.NULL,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport: '_onImport_Leverage',
        },
        {
          cell: 'InvestmentVehicle.fundLeverage.0.leveragePercentageMax',
          cellRename: {
            fundLeverage: 'leverage',
          },
          field: 'leveragePercentageMax',
          valueType: CELL_VALUE_TYPE_CONFIG.NULL,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport: '_onImport_Leverage',
        },
        {
          cell: 'InvestmentVehicle.fundLeverage.0.leverageBasisMultiple',
          cellRename: {
            fundLeverage: 'leverage',
            leverageBasisMultiple: 'leverageBasis',
          },
          field: 'leverageBasis',
          valueType: CELL_VALUE_TYPE_CONFIG.NULL,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport: '_onImport_LeverageBasis',
        },
        {
          cell: 'InvestmentVehicle.fundLeverage.0.leverageMultipleTarget',
          cellRename: {
            fundLeverage: 'leverage',
          },
          field: 'leverageMultipleTarget',
          valueType: CELL_VALUE_TYPE_CONFIG.NULL,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport: '_onImport_Leverage',
        },
        {
          cell: 'InvestmentVehicle.fundLeverage.0.leverageMultipleMin',
          cellRename: {
            fundLeverage: 'leverage',
          },
          field: 'leverageMultipleMin',
          valueType: CELL_VALUE_TYPE_CONFIG.NULL,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport: '_onImport_Leverage',
        },
        {
          cell: 'InvestmentVehicle.fundLeverage.0.leverageMultipleMax',
          cellRename: {
            fundLeverage: 'leverage',
          },
          field: 'leverageMultipleMax',
          valueType: CELL_VALUE_TYPE_CONFIG.NULL,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport: '_onImport_Leverage',
        },
        {
          cell: 'InvestmentVehicle.closedEndDetails.closedEndGPTargetInvestmentStat.numPortfolioCompaniesLow',
          field: 'numPortfolioCompaniesLow',
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
        },
        {
          cell: 'InvestmentVehicle.closedEndDetails.closedEndGPTargetInvestmentStat.numPortfolioCompaniesHigh',
          field: 'numPortfolioCompaniesHigh',
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
        },
        {
          cell: 'InvestmentVehicle.closedEndDetails.closedEndGPTargetInvestmentStat.enterpriseValueRangeLow',
          field: 'enterpriseValueRangeLow',
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
        },
        {
          cell: 'InvestmentVehicle.closedEndDetails.closedEndGPTargetInvestmentStat.enterpriseValueRangeHigh',
          field: 'enterpriseValueRangeHigh',
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
        },
        {
          cell: 'InvestmentVehicle.closedEndDetails.closedEndGPTargetInvestmentStat.equityInvestmentRangeLow',
          field: 'equityInvestmentRangeLow',
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
        },
        {
          cell: 'InvestmentVehicle.closedEndDetails.closedEndGPTargetInvestmentStat.equityInvestmentRangeHigh',
          field: 'equityInvestmentRangeHigh',
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
        },
        {
          cell: 'InvestmentVehicle.closedEndDetails.closedEndGPTargetInvestmentStat.revenueRangeLow',
          field: 'revenueRangeLow',
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
        },
        {
          cell: 'InvestmentVehicle.closedEndDetails.closedEndGPTargetInvestmentStat.revenueRangeHigh',
          field: 'revenueRangeHigh',
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
        },
        {
          cell: 'InvestmentVehicle.closedEndDetails.closedEndGPTargetInvestmentStat.ebitdaRangeLow',
          field: 'ebitdaRangeLow',
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
        },
        {
          cell: 'InvestmentVehicle.closedEndDetails.closedEndGPTargetInvestmentStat.ebitdaRangeHigh',
          field: 'ebitdaRangeHigh',
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
        },
        {
          cell: 'InvestmentVehicle.closedEndDetails.closedEndGPTargetInvestmentStat.transactionMultipleLow',
          field: 'transactionMultipleLow',
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
        },
        {
          cell: 'InvestmentVehicle.closedEndDetails.closedEndGPTargetInvestmentStat.transactionMultipleHigh',
          field: 'transactionMultipleHigh',
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
        },
        {
          cell: 'InvestmentVehicle.closedEndDetails.closedEndGPTargetInvestmentStat.transactionMultipleRevenueLow',
          field: 'transactionMultipleRevenueLow',
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
        },
        {
          cell: 'InvestmentVehicle.closedEndDetails.closedEndGPTargetInvestmentStat.transactionMultipleRevenueHigh',
          field: 'transactionMultipleRevenueHigh',
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
        },
        {
          cell: 'InvestmentVehicle.closedEndDetails.closedEndGPTargetInvestmentStat.boardSeats',
          field: 'boardSeats',
          valueType: CELL_VALUE_TYPE_CONFIG.STRING,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport(value) {
            return SimpleAnswerEnum.toOptions().find(
              (option) => option.label === value,
            )?.value;
          },
        },
        {
          cell: 'InvestmentVehicle.assetLeverage.0.leverageBasisMultiple',
          cellRename: {
            assetLeverage: 'leverage',
            leverageBasisMultiple: 'leverageBasis',
          },
          field: 'leverageBasis',
          valueType: CELL_VALUE_TYPE_CONFIG.STRING,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport: '_onImport_LeverageBasis',
        },
        {
          cell: 'InvestmentVehicle.assetLeverage.0.leverageMultipleTarget',
          cellRename: {
            assetLeverage: 'leverage',
          },
          field: 'leverageMultipleTarget',
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport: '_onImport_Leverage',
        },
        {
          cell: 'InvestmentVehicle.assetLeverage.0.leverageMultipleMin',
          cellRename: {
            assetLeverage: 'leverage',
          },
          field: 'leverageMultipleMin',
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport: '_onImport_Leverage',
        },
        {
          cell: 'InvestmentVehicle.assetLeverage.0.leverageMultipleMax',
          cellRename: {
            assetLeverage: 'leverage',
          },
          field: 'leverageMultipleMax',
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport: '_onImport_Leverage',
        },
        {
          cell: 'InvestmentVehicle.closedEndDetails.closedEndGPTargetInvestmentStat.seedAssets',
          field: 'seedAssets',
          valueType: CELL_VALUE_TYPE_CONFIG.STRING,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport(value) {
            return SimpleAnswerEnum.toOptions().find(
              (option) => option.label === value,
            )?.value;
          },
        },
        {
          cell: 'InvestmentVehicle.closedEndDetails.closedEndGPTargetInvestmentStat.programEquityCommittedSeedAssets',
          field: 'programEquityCommittedSeedAssets',
          valueType: CELL_VALUE_TYPE_CONFIG.NULL,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
        },
        {
          cell: 'InvestmentVehicle.closedEndDetails.closedEndGPTargetInvestmentStat.numSeedInvestments',
          field: 'numSeedInvestments',
          valueType: CELL_VALUE_TYPE_CONFIG.NULL,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
        },
        {
          cell: 'InvestmentVehicle.closedEndDetails.closedEndGPTargetInvestmentStat.maxDevelopment',
          field: 'maxDevelopment',
          valueType: CELL_VALUE_TYPE_CONFIG.NULL,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
        },
        {
          cell: 'InvestmentVehicle.closedEndDetails.closedEndGPTargetInvestmentStat.targetHoldingPeriod',
          field: 'targetHoldingPeriod',
          valueType: CELL_VALUE_TYPE_CONFIG.NULL,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
        },
        {
          cell: 'InvestmentVehicle.assetLeverage.0.leverageBasisPercentage',
          cellRename: {
            assetLeverage: 'leverage',
            leverageBasisPercentage: 'leverageBasis',
          },
          field: 'leverageBasis',
          valueType: CELL_VALUE_TYPE_CONFIG.NULL,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport: '_onImport_LeverageBasis',
        },
        {
          cell: 'InvestmentVehicle.assetLeverage.0.leveragePercentageTarget',
          cellRename: {
            assetLeverage: 'leverage',
          },
          field: 'leveragePercentageTarget',
          valueType: CELL_VALUE_TYPE_CONFIG.NULL,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport: '_onImport_Leverage',
        },
        {
          cell: 'InvestmentVehicle.assetLeverage.0.leveragePercentageMin',
          cellRename: {
            assetLeverage: 'leverage',
          },
          field: 'leveragePercentageMin',
          valueType: CELL_VALUE_TYPE_CONFIG.NULL,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport: '_onImport_Leverage',
        },
        {
          cell: 'InvestmentVehicle.assetLeverage.0.leveragePercentageMax',
          cellRename: {
            assetLeverage: 'leverage',
          },
          field: 'leveragePercentageMax',
          valueType: CELL_VALUE_TYPE_CONFIG.NULL,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          onImport: '_onImport_Leverage',
        },
      ],
    },
  ],
};
